<template>
  <button
    class="youtube-container"
    :disabled="!active"
    :aria-label="isPlaying ? 'stop short' : 'start short'"
    @click="playVideo"
  >
    <div ref="video" tabindex="-1"></div>
    <div :class="['custom-overlay', isPlaying && 'is--playing']">
      <div
        :class="['custom-poster', isPlaying && 'is--playing']"
        :style="`background-image: url(${poster})`"
      >
        <span class="custom-play-button">
          <Icon name="eenvandaag-general:play-solid" aria-hidden="true" role="presentation" />
        </span>
      </div>
    </div>
  </button>
</template>

<script setup lang="ts">
const isPlaying = ref(false);
const video = ref();
const { directusAssetsUrl } = useRuntimeConfig().public;

const { onLoaded } = useScriptYouTubePlayer({});
const player = ref<YT.Player>();

const emit = defineEmits(['complete']);

const props = defineProps({
  poster: {
    type: Object,
    default: () => {},
  },
  videoId: {
    type: String,
    default: '',
  },
  autoplay: {
    type: Boolean,
    default: false,
  },
  play: {
    type: Boolean,
    default: false,
  },
  isDragging: {
    type: Boolean,
    default: false,
  },
  active: {
    type: Boolean,
    default: false,
  },
});

const poster = props.poster?.id
  ? `url(${directusAssetsUrl}${props.poster?.id}.webp?width=720&height=405&fit=cover)`
  : `https://img.youtube.com/vi/${props.videoId}/maxresdefault.jpg`;

onNuxtReady(() => {
  onLoaded(async ({ YT }) => {
    // we need to wait for the internal YouTube APIs to be ready
    const YouTube = await YT;
    await new Promise<void>((resolve) => {
      if (typeof YouTube.Player === 'undefined') YouTube.ready(resolve);
      else resolve();
    });
    // load the API
    player.value = new YouTube.Player(video.value, {
      videoId: props.videoId,
      host: 'https://www.youtube-nocookie.com',
      playerVars: {
        origin: window.location.host,
        color: 'white',
        rel: 0,
        enablejsapi: 1,
        modestbranding: 1,
        showinfo: 0,
        ecver: 2,
        controls: 0,
      },
      events: {
        onReady: () => {
          if (props.autoplay) {
            nextTick(() => {
              if (import.meta.client) {
                if (!iOS()) {
                  playVideo();
                }
              }
            });
          }
        },
        onStateChange: (event: YT.OnStateChangeEvent) => {
          if (event.data === YouTube.PlayerState.PLAYING) {
            isPlaying.value = true;
          }
          if (event.data === YouTube.PlayerState.PAUSED) {
            isPlaying.value = false;
          }
          if (event.data === YouTube.PlayerState.ENDED) {
            emit('complete', true);
          }
        },
      },
    });
  });
});

const iOS = () => {
  return navigator.userAgent.match(/ipad|ipod|iphone/i);
};

const playVideo = () => {
  if (isPlaying.value) {
    player.value?.pauseVideo();
    isPlaying.value = false;
  } else {
    isPlaying.value = true;
    player.value?.playVideo();
  }
};

watch(
  () => props.autoplay,
  () => {
    if (player.value) {
      if (!iOS()) {
        if (props.autoplay === true) {
          playVideo();
        } else {
          player.value?.stopVideo();
          isPlaying.value = false;
        }
      }
    }
  },
);

watch(
  () => props.isDragging,
  () => {
    if (player.value) {
      if (props.isDragging === true) {
        player.value?.pauseVideo();
      }
    }
  },
);

onUnmounted(() => {
  if (player.value) {
    player.value?.destroy();
  }
});
</script>

<style lang="postcss" scoped>
.youtube-container {
  --youtube-player-height: 100%;
  --youtube-player-width: 100%;
  --youtube-player-border-radius: 20px;
  --youtube-player-aspect-ratio: 9/16;
  --youtube-player-controls-height: 12%;

  position: relative;
  border: 0;
  background-color: unset;
  padding: 0;
  width: var(--youtube-player-width);
  height: var(--youtube-player-height);
  margin: auto; /* Center it horizontally */
  overflow: hidden;
  pointer-events: all;

  :deep(iframe) {
    aspect-ratio: var(--youtube-player-aspect-ratio);
    width: var(--youtube-player-width) !important;
    height: var(--youtube-player-height) !important;
  }
}

.custom-overlay {
  border: unset;
  padding: 0;
  position: absolute;
  cursor: pointer;
  z-index: 10;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
  transition: opacity 400ms ease-out 0ms;

  &.is--playing {
    opacity: 0;
  }
}

.custom-play-button {
  --custom-play-button-height: 48px;
  --custom-play-button-width: 70px;
  --custom-play-button-border-radius: 4px;

  display: flex;
  width: var(--custom-play-button-width);
  height: var(--custom-play-button-height);
  justify-content: center;
  align-items: center;
  border-radius: var(--custom-play-button-border-radius);
  background: var(--effect-gradient-brand);
  box-shadow: 0 -1px 0 0 rgba(14, 22, 46, 0.1) inset;

  @media (--lg-viewport) {
    --custom-play-button-height: 57.6px;
    --custom-play-button-width: 84px;
  }
}

.custom-poster {
  background-color: hsl(var(--color-primary-700));
  position: absolute;
  cursor: pointer;
  z-index: 10;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
  opacity: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  background-size: cover;
  background-position: center center;
}
</style>
